import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import pb from '@/lib/pocketbase'
import { useCreateLaporan, useUpdateLaporan } from '@/hooks/useKegiatan'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'
import Select from '@/components/ui/Select'
import Card from '@/components/ui/Card'
import type { KegiatanLaporan } from '@/types/pocketbase'

const jenisOptions = [
  { label: 'Laporan Kegiatan', value: 'kegiatan' },
  { label: 'Evaluasi', value: 'evaluasi' },
  { label: 'Laporan Keuangan', value: 'keuangan' },
  { label: 'Monev', value: 'monev' },
]

const visibilitasOptions = [
  { label: 'Publik', value: 'publik' },
  { label: 'Committee Only', value: 'committee_only' },
  { label: 'Stadion Only', value: 'stadion_only' },
]

export default function LaporanFormPage() {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const isEditing = !!id

  const kegiatanIdFromUrl = searchParams.get('kegiatan') || ''

  const { data: existing } = useQuery({
    queryKey: ['laporan', id],
    queryFn: () => pb.collection('kegiatan_laporan').getOne(id!),
    enabled: !!id,
  }) as { data: KegiatanLaporan | undefined }

  const createMutation = useCreateLaporan()
  const updateMutation = useUpdateLaporan()

  const [form, setForm] = useState({
    judul: '',
    jenis: 'kegiatan',
    konten: '',
    visibilitas: 'publik',
    published: false,
    kegiatan: '',
  })

  useEffect(() => {
    if (existing && isEditing) {
      setForm({
        judul: existing.judul || '',
        jenis: existing.jenis || 'kegiatan',
        konten: existing.konten || '',
        visibilitas: existing.visibilitas || 'publik',
        published: existing.published || false,
        kegiatan: (existing as { kegiatan?: string }).kegiatan || '',
      })
    } else if (kegiatanIdFromUrl) {
      setForm((f) => ({ ...f, kegiatan: kegiatanIdFromUrl }))
    }
  }, [existing, isEditing, kegiatanIdFromUrl])

  const handleSubmit = async (publishNow: boolean) => {
    try {
      const payload: Record<string, unknown> = {
        ...form,
        published: publishNow,
        author: pb.authStore.model?.id || '',
        ...(form.kegiatan && { kegiatan: form.kegiatan }),
        ...(publishNow && { published_at: new Date().toISOString() }),
      }

      if (isEditing && id) {
        await updateMutation.mutateAsync({ id, data: payload })
        navigate('/laporan')
      } else {
        await createMutation.mutateAsync(payload)
        navigate('/laporan')
      }
    } catch (err) {
      console.error('Laporan submit error:', err)
    }
  }

  const loading = createMutation.isPending || updateMutation.isPending

  return (
    <div className="py-4 space-y-4">
      <Card>
        <h2 className="text-base font-bold mb-4">{isEditing ? 'Edit Laporan' : 'Buat Laporan Baru'}</h2>

        <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
          <Select
            label="Jenis Laporan"
            options={jenisOptions}
            value={form.jenis}
            onChange={(e) => setForm({ ...form, jenis: e.target.value })}
          />

          <Input
            label="Judul Laporan"
            value={form.judul}
            onChange={(e) => setForm({ ...form, judul: e.target.value })}
            placeholder="Contoh: Laporan Kegiatan Seminar Dakwah"
          />

          <div>
            <label className="block text-sm font-semibold text-text-primary mb-1.5">Konten</label>
            <textarea
              className="w-full border border-gray-300 rounded-input px-4 py-3 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              rows={8}
              value={form.konten}
              onChange={(e) => setForm({ ...form, konten: e.target.value })}
              placeholder="Tulis konten laporan di sini..."
            />
          </div>

          <Select
            label="Visibilitas"
            options={visibilitasOptions}
            value={form.visibilitas}
            onChange={(e) => setForm({ ...form, visibilitas: e.target.value as typeof form.visibilitas })}
          />

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.published}
              onChange={(e) => setForm({ ...form, published: e.target.checked })}
              className="w-4 h-4 accent-primary"
            />
            <span className="text-sm">Publish sekarang</span>
          </label>

          <div className="pt-2 space-y-2">
            <Button
              type="button"
              block
              variant="outline"
              loading={loading}
              onClick={() => handleSubmit(false)}
            >
              Simpan Draft
            </Button>
            <Button
              type="button"
              block
              loading={loading}
              onClick={() => handleSubmit(true)}
            >
              Simpan & Publish
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}
